import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Space, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
  CheckCircleOutlined,
  CloseSquareOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'

import { path, useColumnSearch } from 'src/libs'
import { getNumberFormat } from 'src/libs/utils'
import { Guard } from 'src/features/session/guard'

import { onChangeParticipantList } from 'src/pages/worldCup2026/participants/model'
import {
  useParticipantList,
  useParticipantListGate,
} from 'src/pages/worldCup2026/participants/hooks'
import {
  AddCoinsModal,
  BanModal,
} from 'src/pages/worldCup2026/participants/widgets'
import { Participant } from 'src/pages/worldCup2026/participants/types'
import { LEVEL_FILTER } from 'src/pages/worldCup2026/const'
import { Client } from 'src/pages/worldCup2026/types'

export const ParticipantList = () => {
  const [clientBan, setClientBan] = useState<Client | null>(null)
  const [clientAddCoins, setClientAddCoins] = useState<Client | null>(null)
  const { participants, loading, totalCountOfParticipants } =
    useParticipantList()
  useParticipantListGate()

  const clientIdSearch = useColumnSearch<Participant>({
    placeholder: 'clientID',
    customRender: (highlightedValue, record) => (
      <Link
        to={path.clients.details(String(record.client.clientId))}
        style={{ textDecoration: 'none' }}
      >
        {highlightedValue}
      </Link>
    ),
  })

  const clientFullNameSearch = useColumnSearch<Participant>({
    placeholder: 'client Fullname',
  })
  const clientEmailSearch = useColumnSearch<Participant>({
    placeholder: 'client Email',
  })

  const columns: ColumnsType<Participant> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      ...clientIdSearch,
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...clientFullNameSearch,
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...clientEmailSearch,
    },
    {
      title: 'Is Partner',
      dataIndex: ['client', 'partner'],
      filters: [
        {
          text: 'Yes',
          value: true,
        },
        {
          text: 'No',
          value: false,
        },
      ],
      filterMultiple: false,
      render: (isPartner: boolean) => (
        <>
          {isPartner ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          )}
        </>
      ),
    },
    {
      title: 'Tickets',
      dataIndex: 'allTickets',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (item, record) => (
        <div>
          {item}{' '}
          <Tooltip
            title={
              <div>
                <div>Trading: {record.ticketsCount}</div>
                <div>New client: {record.clientsFirstTicketsCount}</div>
                <div>Client trading: {record.clientsOverallTicketsCount}</div>
                <div>Tasks: {record.taskTicketsCount}</div>
              </div>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Coins',
      dataIndex: 'sumPitchPrice',
      sorter: true,
      render: (item, record) => (
        <div>
          {item}{' '}
          <Tooltip
            title={
              <div>
                <div>Squad value: {record.sumPitchPrice}</div>
                <div>Tasks: {record.taskPoints}</div>
                <div>Manual transfers: {record.sumManualTransfers}</div>
                <div>Total earned: {record.allPoints}</div>
                <div>Total spent: {record.coinsSpent}</div>
              </div>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      filters: LEVEL_FILTER,
      filterMultiple: true,
    },
    {
      title: 'Tasks',
      dataIndex: 'completedTasks',
      sorter: true,
    },
    {
      title: 'Lots',
      dataIndex: 'lots',
      sorter: true,
      render: (lots: string) => lots && getNumberFormat(lots, 2),
    },
    {
      title: 'Status',
      key: 'status',
      render: (item: Participant) => {
        return item.bannedAt ? item.banReason : item.status
      },
      filterMultiple: false,
      filters: [
        {
          text: 'active',
          value: 'active',
        },
        {
          text: 'ban',
          value: 'disqualified',
        },
      ],
    },
    {
      title: 'Actions',
      align: 'center',
      fixed: 'right',
      width: 150,
      render: (item: Participant) => {
        const isActive = !item.bannedAt
        return (
          <Space>
            <Guard permission="world_cup_2026_ban">
              <Button
                color="blue"
                type="primary"
                onClick={() => setClientAddCoins(item.client)}
              >
                Add Coins
              </Button>
            </Guard>
            {isActive ? (
              <Guard permission="world_cup_2026_ban">
                <Button
                  danger
                  type="primary"
                  onClick={() => setClientBan(item.client)}
                >
                  Disqualification
                </Button>
              </Guard>
            ) : null}
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <Table
        dataSource={participants}
        bordered={true}
        className="overflow-auto"
        loading={loading}
        rowKey={(record) => record.client.clientId}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfParticipants,
        }}
        onChange={(pagination, filters, sorter: any) => {
          onChangeParticipantList({
            page: pagination.current,
            limit: pagination.pageSize,
            orderBy: sorter?.field || sorter?.columnKey,
            orderDirection: sorter?.order,
            filters,
          })
        }}
      />
      {clientBan && (
        <BanModal client={clientBan} onClose={() => setClientBan(null)} />
      )}
      {clientAddCoins && (
        <AddCoinsModal
          client={clientAddCoins}
          onClose={() => setClientAddCoins(null)}
        />
      )}
    </>
  )
}
